import { ChakraProvider } from "@chakra-ui/react";
import { Global } from "@emotion/react";
import * as Sentry from "@sentry/nextjs";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import RelativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { isMobile, isMobileOnly, isTablet } from "react-device-detect";
import "react-toastify/dist/ReactToastify.css";
import { RecoilRoot, useRecoilState, useSetRecoilState } from "recoil";
import "typeface-lato";
import "typeface-maven-pro";
import "typeface-source-sans-pro";
import "typeface-work-sans";
import { ContactApi } from "../api/Contacts";
import { UserAPI } from "../api/User";
import { TestDriveBannerAtom } from "../atoms/TestDriveBannerAtom";
import {
  InternalUserAtom,
  UserAtom,
  UserCustomCopyTemplates,
} from "../atoms/UserAtom";
import MobileModal from "../components/Modal/container/MobileModal";
import Modal from "../components/Modal/Modal";
import PageLoading from "../components/PageLoading";
import { MyToastContainer } from "../components/Toast";
import { SERVER_URL } from "../config";
import { useResetAllRecoil } from "../hook/useResetAllRecoil";
import Layout from "../layout/Layout";
import "../styles/calendarLinks.css";
import "../styles/form-checkbox.css";
import GlobalStyle from "../styles/global";
import "../styles/noteCheckbox.css";
import "../styles/react-calendar.css";
import { TEST_USERS } from "../utils/constants";
import { checkCurrentVersion } from "../utils/CurrentVersion";
import {
  localStorageClear,
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from "../utils/LocalStorage";
import "../utils/TippyPureHTML.css";
import { newTheme } from "./../styles/theme";
import Main from "./Main";

dayjs.extend(LocalizedFormat);
dayjs.extend(RelativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(CustomParseFormat);

// screens that don't need to be logged in
const ScreensDontNeedLoggedIn = [
  "/__admin",
  "/email-error",
  "/confirmed",
  "/form-builder",
  "/internal-form",
  "/payment",
  "/signup",
  "/reset-passcode",
  "/unsubscribe",
  "/form",
  "/thank-you-purchase",
  "/thank-you-builder",
];

const MOBILE_SCREENS = [
  "/unsubscribe",
  "/email-error",
  "/confirmed",
  "/form-builder",
  "/internal-form",
  "/form",
];

function MyApp(props: {
  setIsLoading: (value: React.SetStateAction<boolean>) => void;
}) {
  const router = useRouter();
  const [userInfo, setUserInfo] = useRecoilState(UserAtom);
  const setCustomCopyTemplates = useSetRecoilState(UserCustomCopyTemplates);
  const setInternalUser = useSetRecoilState(InternalUserAtom);
  const setTestDriveBanner = useSetRecoilState(TestDriveBannerAtom);
  const tokenTimestamp = useRef(null);
  const internalTokenTimestamp = useRef(null);
  const resetRecoil = useResetAllRecoil();

  const userUid = userInfo?.userUid;

  function logout() {
    localStorageClear();
    resetRecoil();
    router.replace("/");
  }

  function renewInternalUserToken(
    internalUserUid: string,
    intenalToken: string
  ) {
    if (internalTokenTimestamp.current)
      clearInterval(internalTokenTimestamp.current);
    internalTokenTimestamp.current = setInterval(async () => {
      try {
        if (!internalUserUid || !intenalToken) return;
        const response = await fetch(
          `${SERVER_URL}/auth/internal_token/${internalUserUid}`,
          {
            method: "GET",
            headers: {
              Authorization: "Bearer " + intenalToken,
            },
          }
        );
        const responseJSON = await response.json();
        if (response.ok && responseJSON.data?.token) {
          localStorageSetItem("INTERNAL_TOKEN", responseJSON.data.token);
        }
      } catch (error) {}
    }, 5 * 60 * 1000);
  }

  useEffect(() => {
    // Redefining the fetch so it includes the rules for JWT handling
    const fetch = window.fetch;
    window.fetch = (...args) =>
      (async (args) => {
        var result = await fetch(...args);
        if (result && result.status === 401) {
          // If I'm an internal user, just ignore it
          // let isInternalUser = !!localStorageGetItem("INTERNAL_USER_UID");
          // if (isInternalUser) return;

          localStorageClear();
          if (process.env.NEXT_PUBLIC_IS_SUBDOMAIN !== "TRUE")
            router.replace("/");
        } else {
          return result;
        }
      })(args);

    function onGlobalError(e: ErrorEvent) {
      if (e.error.name === "AbortError") return;
      Sentry.captureException(e.error);
    }

    window.addEventListener("error", onGlobalError);

    return () => {
      window.removeEventListener("error", onGlobalError);
    };
  }, []);

  useEffect(() => {
    // Redefining the command for Ctrl + S so I can access the account selection page
    function handleShortcutPress(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();

        // If I'm not an internal user, there's no use for it
        let isInternalUser = !!localStorageGetItem("INTERNAL_USER_UID");
        if (!isInternalUser) return;

        // If I navigated to the screen via a shortcut, go back
        if (router.asPath.includes("/account-selection?shortcut=true")) {
          router.back();
        } else if (!router.asPath.includes("/account-selection")) {
          // Else, if I'm not there still, let's go
          router.push("/account-selection?shortcut=true");
        }

        return false;
      }
    }

    // DO NOT DELETE DO NOT DELETE DO NOT DELETE DO NOT DELETE
    // DO NOT DELETE DO NOT DELETE DO NOT DELETE DO NOT DELETE
    // DO NOT DELETE DO NOT DELETE DO NOT DELETE DO NOT DELETE
    // DO NOT DELETE DO NOT DELETE DO NOT DELETE DO NOT DELETE
    // Waiting for a better than Ctrl + S combination
    // window.addEventListener("keydown", handleShortcutPress);

    return () => {
      // window.removeEventListener("keydown", handleShortcutPress);
    };
  }, [router.asPath]);

  useEffect(() => {
    if (tokenTimestamp.current) clearInterval(tokenTimestamp.current);
    tokenTimestamp.current = setInterval(async () => {
      try {
        const currentToken = localStorageGetItem("USER_TOKEN");
        if (!userUid || !currentToken) return;
        const response = await fetch(`${SERVER_URL}/auth/token`, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + currentToken,
          },
        });
        const responseJSON = await response.json();
        if (response.ok && responseJSON.data?.token) {
          localStorageSetItem("USER_TOKEN", responseJSON.data.token);
        }
      } catch (error) {}
    }, 5 * 60 * 1000);
  }, [userUid]);

  useEffect(() => {
    let href = window.location.href;
    if (
      href.split("//")[1].split(":")[0] !== "localhost" &&
      href.split("//")[1].split(".")[0] !== "app" &&
      href.split("//")[1].split(".")[0] !== "staging" &&
      href.split("//")[1].split(".")[0] !== "demo"
    ) {
      return;
    }

    const userUid =
      (localStorageGetItem("INTERNAL_USER_UID") as string) ||
      (localStorageGetItem("USER_UID") as string);

    const userToken =
      (localStorageGetItem("INTERNAL_TOKEN") as string) ||
      (localStorageGetItem("USER_TOKEN") as string);

    const isTestUser = TEST_USERS.includes(userUid);

    let unsubscribeRegexTest = false;
    MOBILE_SCREENS.forEach((path) => {
      if (
        new RegExp(path) instanceof RegExp &&
        new RegExp(path).test(router.asPath)
      ) {
        unsubscribeRegexTest = true;
      }
    });

    const isLoginPage = "/" === router.asPath;

    if (
      !isLoginPage &&
      !isTestUser &&
      isMobile &&
      !isTablet &&
      !(MOBILE_SCREENS.includes(router.asPath) || unsubscribeRegexTest)
    ) {
      if ("/mobile-device" !== router.asPath.split("?")[0]) {
        router.replace("/mobile-device");
        return;
      }
    }

    let regexTest = false;

    ScreensDontNeedLoggedIn.forEach((path) => {
      if (
        new RegExp(path) instanceof RegExp &&
        new RegExp(path).test(router.asPath)
      ) {
        regexTest = true;
      }
    });

    if (
      (!userUid || !userToken) &&
      !(ScreensDontNeedLoggedIn.includes(router.asPath) || regexTest)
    ) {
      if (process.env.NEXT_PUBLIC_IS_SUBDOMAIN !== "TRUE") router.replace("/");
    }
  }, []);

  useEffect(() => {
    if (userInfo) return;
    props.setIsLoading(true);
    const userUid = (localStorageGetItem("USER_UID") ||
      localStorageGetItem("INTERNAL_USER_UID")) as string;
    const userToken = (localStorageGetItem("USER_TOKEN") ||
      localStorageGetItem("INTERNAL_TOKEN")) as string;
    let doesScreenNeedLoginRegexTest = false;
    ScreensDontNeedLoggedIn.forEach((path) => {
      if (
        new RegExp(path) instanceof RegExp &&
        new RegExp(path).test(router.asPath)
      ) {
        doesScreenNeedLoginRegexTest = true;
      }
    });

    if (
      (!userUid || !userToken) &&
      !(
        ScreensDontNeedLoggedIn.includes(router.asPath) ||
        doesScreenNeedLoginRegexTest
      )
    ) {
      props.setIsLoading(false);
      if (process.env.NEXT_PUBLIC_IS_SUBDOMAIN !== "TRUE") router.replace("/");
      return;
    }

    (async () => {
      if (
        ScreensDontNeedLoggedIn.includes(router.asPath) ||
        doesScreenNeedLoginRegexTest
      ) {
        props.setIsLoading(false);
        return;
      }

      // If I'm an internal user, load it into the memory so I can
      // work with it when the user gets out of the account
      let internalUserUid = localStorageGetItem("INTERNAL_USER_UID") as string;
      let internalToken = localStorageGetItem("INTERNAL_TOKEN") as string;
      if (internalUserUid) {
        renewInternalUserToken(internalUserUid, internalToken);

        const internalUser = await UserAPI.getInternalUser(internalUserUid);

        if (internalUser) setInternalUser(internalUser);
        else logout();

        // On this specific screen, I only need to have the internal user
        if (window.location.href.includes("/account-selection")) {
          props.setIsLoading(false);
          return;
        }
      }

      const user = await UserAPI.getUser(userUid);

      // Check if the user needs to complete the onboarding
      if (user) {
        if (user.isActive) {
          // Get the user templates
          const userCustomCopyTemplates =
            await UserAPI.getCustomCopyTemplates();

          if (userCustomCopyTemplates.length === 0) {
            setCustomCopyTemplates([
              {
                title: "Default",
                template: "",
                userUid: user.userUid,
              },
            ]);
          } else {
            setCustomCopyTemplates(userCustomCopyTemplates);
          }

          // Set the banners and such

          setTestDriveBanner({
            show: !user.billing?.active && user.billing?.testDriveActivated,
            testDriveExpiration: user.billing?.testDriveExpiration || 0,
          });

          setUserInfo(user);

          props.setIsLoading(false);
        } else if (!user.isActive && userUid) {
          props.setIsLoading(false);
          if (process.env.NEXT_PUBLIC_IS_SUBDOMAIN !== "TRUE")
            router.replace("/");
          localStorageClear();
          return;
        }

        let needsToCompleteCANSPAM =
          user.company.trim() === "" && user.address.trim() === "";

        let isOnTestDrive =
          !user.billing?.active && user.billing?.testDriveActivated;

        let needsToCreateFirstContact = false;

        if (isOnTestDrive) {
          let contactWithTestDriveTag = await ContactApi.getTestDriveContact();
          if (!contactWithTestDriveTag.exists) needsToCreateFirstContact = true;
        }

        if (needsToCompleteCANSPAM || needsToCreateFirstContact) {
          props.setIsLoading(false);
          router.replace("/complete-onboarding");
        }
      }
    })();
  }, [userInfo]);

  useEffect(() => {
    // If I'm a managed users detached, I go to the select account screen
    const internalUserUid = localStorageGetItem("INTERNAL_USER_UID");
    const internalUserToken = localStorageGetItem("INTERNAL_TOKEN");

    const userUid = localStorageGetItem("USER_UID");
    const userToken = localStorageGetItem("USER_TOKEN");

    if (
      router.asPath === "/account-selection" &&
      internalUserUid &&
      internalUserToken
    ) {
      props.setIsLoading(false);
      return;
    }

    if (!userUid && internalUserUid) {
      router.replace("/account-selection");
      return;
    }

    if (router.asPath === "/" && userUid && userToken) {
      router.push("/new-email");
      return;
    }

    if (router.asPath === "/" && !userUid && !internalUserUid) {
      props.setIsLoading(false);
      return;
    }
  }, [router.asPath]);

  useEffect(() => {
    global.ps_config = {
      workspace_id: "f8ea5b12-675a-43e1-af7e-19f0464c62d8",
    };

    let script = document.createElement("script");
    script.setAttribute("type", "text/javascript");
    script.setAttribute("src", "https://cdn.loopedin.io/js/widget.js?v=0.8");
    document.getElementsByTagName("head")[0].appendChild(script);
  }, [router]);

  return null;
}

const ROUTES_WITH_SIDEBAR = [
  "/new-email",
  "/edit-email",
  "/dashboard",
  "/emails",
  "/email-report",
  "/modal",
  "/contacts",
  "/contacts/import-emails",
  "/settings",
  "/tags",
  "/campaigns",
  "/lists",
  "/dev/icon",
  "/all-forms",
  "/campaign",
  "/new-campaign",
  "/create-campaign",
  "/users",
  "/contacts/test",
  "/form-builder",
  "/contacts/details",
  "/automation-triggers",
  "/armory",
  "/armory/video",
];

function Root({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [canRotate, setCanRotate] = useState(false);

  const [isFirstLoading, setIsFirstLoading] = useState(true);

  useEffect(() => {
    if (typeof window.location.host === "string") {
      setCanRotate(
        window.location.host.includes("app.berserkermail.com") ||
          window.location.host.includes("staging.berserkermail.com")
      );
    }
  }, []);

  useEffect(() => {
    const handleWindowResize = () => {
      setWidth(
        window?.innerWidth ||
          document?.documentElement?.clientWidth ||
          document?.body?.clientWidth
      );
      setHeight(
        window?.innerHeight ||
          document?.documentElement?.clientHeight ||
          document?.body?.clientHeight
      );
    };

    // component is mounted and window is available
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);
    // unsubscribe from the event on component unmount
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [width, height]);

  useEffect(() => {
    // If I'm not on one of our envs, do NOT redirect
    let href = window.location.href;
    if (
      href.split("//")[1].split(":")[0] !== "localhost" &&
      href.split("//")[1].split(".")[0] !== "app" &&
      href.split("//")[1].split(".")[0] !== "staging" &&
      href.split("//")[1].split(".")[0] !== "demo"
    ) {
      setIsFirstLoading(false);
      return;
    }

    setIsFirstLoading(false);
  }, []);

  // Every 60 seconds, check if the version matches
  useEffect(() => {
    checkCurrentVersion();
    localStorageRemoveItem("ATOM_CURRENT_RECIPIENTS");
    localStorageRemoveItem("ATOM_RECIPIENT");
    let interval = setInterval(() => {
      checkCurrentVersion();
    }, 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const ComponentContent = useMemo(() => {
    if (ROUTES_WITH_SIDEBAR.includes(router.asPath.split("?")[0])) {
      return (
        <Layout>
          <Component {...pageProps} />
        </Layout>
      );
    } else {
      return <Component {...pageProps} />;
    }
  }, [router.asPath]);

  return (
    <RecoilRoot>
      <Global styles={GlobalStyle} />
      <ChakraProvider theme={newTheme} resetCSS={false}>
        <Modal />
        <MyApp setIsLoading={setIsLoading} />
        <MyToastContainer />

        {isLoading ? (
          <PageLoading />
        ) : (
          <Main ComponentContent={ComponentContent} />
        )}

        {canRotate && isMobileOnly && width / height >= 1 && <MobileModal />}
      </ChakraProvider>
    </RecoilRoot>
  );
}

export default Root;
